/**
 * Shared visual contract for the virtualized div-grids on the frontend.
 *
 * Six grids (Trades, MTM, Inventory, Availability, RecallReport,
 * AvailabilityReport) implement their own row-virtualised div layout. The
 * chrome (sticky header colour / border / typography) and the trade-mode
 * row / header heights were copy-pasted across all of them, which caused
 * silent typography drift — LR-368 surfaced MtmTable diverging from
 * `text-sm font-medium` to `text-xs font-semibold`.
 *
 * This module centralises just the style tokens. It does NOT extract a
 * wrapper component — that's a follow-up (VirtualizedGrid). Per LR-383
 * the grids must remain pixel-identical after migration.
 *
 * Per-table specifics intentionally kept inline:
 * - InventoryTable uses its own header height (56px) and row height (38px).
 * - RecallReportTable / AvailabilityReportTable pad the header via px-4 py-2
 *   instead of a fixed 41px and use 38px rows.
 *
 * LR-383.
 */

/**
 * Sticky table header chrome — colour, border, typography. Shared by all
 * six grids. Each grid adds its own layout primitives on top
 * (shrink-0 + fixed height, or px-4 py-2 padding).
 */
export const GRID_HEADER_CHROME =
  'sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-900';

/** Default header height for trade-mode grids (Trades, MTM, Availability). */
export const GRID_HEADER_HEIGHT_PX = 41;

/** Inline style helper for {@link GRID_HEADER_HEIGHT_PX}. */
export const GRID_HEADER_HEIGHT_STYLE = {
  height: `${GRID_HEADER_HEIGHT_PX}px`,
  minHeight: `${GRID_HEADER_HEIGHT_PX}px`,
} as const;

/** Default data-row height for trade-mode grids (Trades, MTM, Availability). */
export const GRID_ROW_HEIGHT_PX = 39;

/** Inline style helper for {@link GRID_ROW_HEIGHT_PX}. */
export const GRID_ROW_HEIGHT_STYLE = {
  height: `${GRID_ROW_HEIGHT_PX}px`,
} as const;
